import { ReactLocalization, useLocalization } from "@fluent/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Drawer, Row, useBreakpoints } from "@narmi/design_system";
import { default as ByzantineAddress } from "byzantine/src/Address";
import { useEffect } from "react";
import { useForm, useWatch } from "react-hook-form";
import { z } from "zod";
import { AddressInput } from "@lending/components/AddressInput/AddressInput";
import { Checkbox } from "@lending/components/Checkbox/Checkbox";
import { DateInput } from "@lending/components/DateInput/DateInput";
import { PhoneNumberInput } from "@lending/components/PhoneNumberInput/PhoneNumberInput";
import { TextInput } from "@lending/components/TextInput/TextInput";
import { useGetApplicationApplicantDetailsSensitiveQuery } from "@lending/hooks/queries/ApplicationApplicantDetails/useGetApplicationApplicantDetailsSensitiveQuery";
import { useListApplicationApplicantDetailsQuery } from "@lending/hooks/queries/ApplicationApplicantDetails/useListApplicationApplicantDetailsQuery";
import { usePutApplicationApplicantDetailsQuery } from "@lending/hooks/queries/ApplicationApplicantDetails/usePutApplicationApplicantDetailsQuery";
import { useGetPersonalLoanApplicationQuery } from "@lending/hooks/queries/PersonalLoanApplication/useGetPersonalLoanApplicationQuery";
import { useGetInstitutionQuery } from "@lending/hooks/queries/useGetInstitutionQuery";
import { useApiErrorToast } from "@lending/hooks/useApiErrorToast";
import {
  buildMailingAddress,
  createDefaultValues as createAddressDefaultValues,
} from "@lending/pages/Common/Address/helpers";
import {
  AddressFormData,
  ApplicantDetailsAddressType,
  ApplicantSensitiveData,
  ApplicationApplicantDetailsRead,
} from "@lending/types";
import {
  applyFieldErrors,
  getApplicantDetailsFieldErrors,
} from "@lending/utils/applicantDetailsErrors";
import { createValidatePhone, createValidateZipCode } from "@lending/utils/validators";
import styles from "./EditInfoDrawer.module.css";

// The read type no longer carries the sensitive fields (tax_id, date_of_birth are write-only,
// fetched via the /sensitive action); merge them in for the editable form.
type EditInfoDrawerFormData = ApplicationApplicantDetailsRead &
  AddressFormData &
  Pick<ApplicantSensitiveData, "tax_id" | "date_of_birth">;

interface EditInfoDrawerProps {
  applicationUuid: string;
  isDrawerOpen: boolean;
  setIsDrawerOpen: (value: boolean) => void;
  lendingApplicant: ApplicationApplicantDetailsRead;
}

function setAddressErrors(
  address: ApplicantDetailsAddressType,
  path: "residential_address" | "mailing_address",
  ctx: z.RefinementCtx,
  requiredMessage: string
) {
  if (!address.street_address) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: requiredMessage,
      path: [path, "street_address"],
    });
  }
  if (!address.city) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: requiredMessage, path: [path, "city"] });
  }
  if (!address.region_code) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: requiredMessage,
      path: [path, "region_code"],
    });
  }
}

function createSchema(l10n: ReactLocalization, askMailingAddress: boolean) {
  const requiredMessage = l10n.getString("error-required", null, "Required");
  const invalidEmailMessage = l10n.getString("error-invalid-email", null, "Enter a valid email");
  const addressSchema = z.object({
    street_address: z.string().min(1, { message: requiredMessage }),
    street_address_2: z.string(),
    city: z.string().min(1, { message: requiredMessage }),
    region_code: z.string().min(1, { message: requiredMessage }),
    postal_code: createValidateZipCode(l10n),
  });
  const partialAddressSchema = z.object({
    street_address: z.string().default(""),
    street_address_2: z.string().default(""),
    city: z.string().default(""),
    region_code: z.string().default(""),
    postal_code: z.string().default(""),
  });

  return z
    .object({
      first_name: z.string().min(1, { message: requiredMessage }),
      last_name: z.string().min(1, { message: requiredMessage }),
      email: z
        .string()
        .min(1, { message: requiredMessage })
        .email({ message: invalidEmailMessage }),
      phone: createValidatePhone(l10n),
      residential_address: addressSchema,
      mailing_address: partialAddressSchema,
      hasSameMailingAddress: z.boolean(),
    })
    .superRefine((value, ctx) => {
      if (askMailingAddress && !value.hasSameMailingAddress) {
        setAddressErrors(value.mailing_address, "mailing_address", ctx, requiredMessage);

        const zipCodeValidation = createValidateZipCode(l10n).safeParse(
          value.mailing_address.postal_code
        );
        if (!zipCodeValidation.success) {
          zipCodeValidation.error.issues.forEach((issue) => {
            ctx.addIssue({ ...issue, path: ["mailing_address", "postal_code"] });
          });
        }
      }
    });
}

function createDefaultValues(applicant: ApplicationApplicantDetailsRead) {
  const addressValues = createAddressDefaultValues(applicant);

  return {
    first_name: applicant.first_name ?? "",
    last_name: applicant.last_name ?? "",
    // Sensitive fields are shown read-only from the /sensitive endpoint (SSN comes back masked) and
    // are filled in separately once that request resolves; they are not part of the applicant read.
    tax_id: "",
    date_of_birth: "",
    phone: applicant.phone ?? "",
    email: applicant.email ?? "",
    residential_address: addressValues.residential_address,
    mailing_address: addressValues.mailing_address,
    hasSameMailingAddress: addressValues.hasSameMailingAddress,
  };
}

function EditInfoDrawer(props: EditInfoDrawerProps) {
  const { l10n } = useLocalization();
  const { institution } = useGetInstitutionQuery();
  const askMailingAddress = Boolean(institution.lending_ask_mailing_address);
  const schema = createSchema(l10n, askMailingAddress);
  const { s } = useBreakpoints();
  const { data: sensitive } = useGetApplicationApplicantDetailsSensitiveQuery(
    props.lendingApplicant.id
  );
  const handleApiError = useApiErrorToast();
  const { handleSubmit, control, formState, reset, setError, setValue } =
    useForm<EditInfoDrawerFormData>({
      defaultValues: createDefaultValues(props.lendingApplicant),
      resolver: zodResolver(schema),
    });
  const hasSameMailingAddress = useWatch({ control, name: "hasSameMailingAddress" });

  const { refetch: refetchGetApplication } = useGetPersonalLoanApplicationQuery({
    enabled: false,
    applicationUuid: props.applicationUuid,
  });

  const onUpdate = (address: ByzantineAddress) => {
    setValue("residential_address", address);
  };

  const onMailingAddressUpdate = (address: ByzantineAddress) => {
    setValue("mailing_address", address);
  };

  const { refetch: refetchApplicants } = useListApplicationApplicantDetailsQuery({
    applicationUuid: props.applicationUuid,
  });

  const { mutate, isPending } = usePutApplicationApplicantDetailsQuery({
    onSuccess: async () => {
      await refetchApplicants();
      props.setIsDrawerOpen(false);
      void refetchGetApplication();
    },
    onError: (error) => {
      const fieldErrors = getApplicantDetailsFieldErrors(error);
      if (fieldErrors.length === 0) handleApiError(error);
      applyFieldErrors(setError, fieldErrors);
    },
  });

  useEffect(() => {
    if (props.isDrawerOpen) {
      const values = createDefaultValues(props.lendingApplicant);
      reset(values);
    }
  }, [props.isDrawerOpen, props.lendingApplicant, reset]);

  // Fill the read-only SSN/DOB display fields once /sensitive resolves. Kept out of the reset()
  // above so a slow response can't wipe edits the user made to the editable fields meanwhile.
  useEffect(() => {
    if (props.isDrawerOpen && sensitive) {
      setValue("tax_id", sensitive.tax_id ?? "");
      setValue("date_of_birth", sensitive.date_of_birth ?? "");
    }
  }, [props.isDrawerOpen, sensitive, setValue]);

  function onSubmit(data: EditInfoDrawerFormData) {
    const anyFieldDirty = Object.keys(formState.dirtyFields).length > 0;

    if (formState.isDirty || anyFieldDirty) {
      const mailing_address = buildMailingAddress(
        data,
        props.lendingApplicant.mailing_address,
        askMailingAddress
      );

      mutate({
        params: {
          path: {
            uuid: props.lendingApplicant.id,
          },
        },
        body: {
          ...props.lendingApplicant,
          first_name: data.first_name,
          last_name: data.last_name,
          phone: data.phone,
          email: data.email,
          residential_address: {
            ...data.residential_address,
            country_code: "US",
          },
          mailing_address,
          application: props.applicationUuid,
        },
      });
      return;
    }

    props.setIsDrawerOpen(false);
  }

  return (
    <Drawer
      depth="40rem"
      paddingSize={s ? "xl" : "m"}
      showControls={false}
      onUserDismiss={() => props.setIsDrawerOpen(false)}
      isOpen={props.isDrawerOpen}
    >
      <form className={styles.form} onSubmit={handleSubmit(onSubmit)} noValidate>
        <div className={styles.formContent}>
          <h2 className="margin--bottom--xl">
            {l10n.getString("edit-info-your-info-heading", null, "Your info")}
          </h2>
          <div className="margin--bottom--xl">
            <div className="fontWeight--semibold margin--bottom--m">
              {l10n.getString("edit-info-details-section", null, "Details")}
            </div>
            <Row className="margin--bottom--l">
              <Row.Item>
                <TextInput
                  name="first_name"
                  control={control}
                  label={l10n.getString("edit-info-first-name", null, "First name")}
                />
              </Row.Item>
              <Row.Item>
                <TextInput
                  name="last_name"
                  control={control}
                  label={l10n.getString("edit-info-last-name", null, "Last name")}
                />
              </Row.Item>
            </Row>
            <Row className="margin--bottom--l">
              <Row.Item>
                <TextInput
                  name="tax_id"
                  control={control}
                  label={l10n.getString("edit-info-ssn", null, "Social Security Number")}
                  disabled
                />
              </Row.Item>
              <Row.Item>
                <DateInput
                  name="date_of_birth"
                  control={control}
                  label={l10n.getString("edit-info-dob", null, "Date of birth")}
                  disabled
                />
              </Row.Item>
            </Row>
            <Row className="margin--bottom--l">
              <Row.Item>
                <TextInput
                  name="email"
                  control={control}
                  label={l10n.getString("edit-info-email", null, "Email")}
                  type="email"
                />
              </Row.Item>
            </Row>
            <Row>
              <Row.Item>
                <PhoneNumberInput
                  name="phone"
                  control={control}
                  label={l10n.getString("edit-info-phone", null, "Phone number")}
                />
              </Row.Item>
            </Row>
          </div>
          <div>
            <div className="fontWeight--semibold margin--bottom--m">
              {l10n.getString("form-subheader-residential-address", null, "residential")}
            </div>
            <div className={askMailingAddress ? "margin--bottom--l" : "margin--bottom--xxl"}>
              <AddressInput
                onUpdate={onUpdate}
                name="residential_address"
                control={control}
                isBreakpoint={s}
              />
            </div>
            {askMailingAddress && (
              <div className="margin--bottom--xxl">
                <div className="margin--bottom--xl fontColor--secondary">
                  <Checkbox
                    name="hasSameMailingAddress"
                    control={control}
                    label={l10n.getString(
                      "address-same-as-mailing-checkbox-label",
                      null,
                      "Residential address same as mailing address"
                    )}
                  />
                </div>
                {!hasSameMailingAddress && (
                  <div>
                    <div className="fontWeight--semibold margin--bottom--m">
                      {l10n.getString("edit-info-mailing-address-section", null, "mailing")}
                    </div>
                    <AddressInput
                      onUpdate={onMailingAddressUpdate}
                      name="mailing_address"
                      control={control}
                      isBreakpoint={s}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        <div>
          <div className={styles.footerDivider} />
          <div className={styles.footerButtons}>
            <Row alignItems="center" justifyContent="space-between">
              <Button
                as="button"
                kind="negative"
                label={l10n.getString("edit-info-button-cancel", null, "Cancel")}
                onClick={() => props.setIsDrawerOpen(false)}
              />
              <Button
                label={l10n.getString("edit-info-button-save", null, "Save")}
                type="submit"
                isLoading={isPending}
              />
            </Row>
          </div>
        </div>
      </form>
    </Drawer>
  );
}

export { EditInfoDrawer };
